/* Dallah Esencia launch page: ruby-gold smoke, the lamp assembling on scroll, rub to light,
   the details, the notes and the waitlist share card. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isSmall = () => window.innerWidth < 990;
  const hasGsap = () => !reduceMotion && window.gsap && window.ScrollTrigger;

  document.documentElement.classList.add('de-js');

  /* ---------- Smoke: one fixed canvas shared by every section ---------- */

  // How often each colour is picked: [ruby, rose, gold].
  const TINTS = {
    lamp: [0.4, 0.3, 0.3],
    gold: [0.12, 0.13, 0.75],
    rose: [0.2, 0.7, 0.1],
    ember: [0.6, 0.1, 0.3],
  };

  const Smoke = {
    canvas: null,
    ctx: null,
    scale: 0.5, // smoke is soft, so half resolution looks the same and costs a quarter
    particles: [],
    emitters: new Set(),
    sprites: [],
    tint: TINTS.lamp,
    running: false,
    last: 0,

    init() {
      if (this.canvas || reduceMotion) return;
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'de-smoke';
      this.canvas.setAttribute('aria-hidden', 'true');
      document.body.appendChild(this.canvas);
      this.ctx = this.canvas.getContext('2d');
      this.sprites = [
        [176, 24, 48],
        [224, 96, 122],
        [232, 193, 112],
      ].map((rgb) => this.makeSprite(rgb));
      this.resize();
      window.addEventListener('resize', () => this.resize());
      window.addEventListener('pointermove', (e) => this.trail(e), { passive: true });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.wake();
      });
    },

    makeSprite([r, g, b]) {
      const s = document.createElement('canvas');
      s.width = s.height = 64;
      const c = s.getContext('2d');
      const grad = c.createRadialGradient(32, 32, 0, 32, 32, 32);
      grad.addColorStop(0, `rgba(${r},${g},${b},1)`);
      grad.addColorStop(0.45, `rgba(${r},${g},${b},0.35)`);
      grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
      c.fillStyle = grad;
      c.fillRect(0, 0, 64, 64);
      return s;
    },

    resize() {
      this.canvas.width = Math.ceil(window.innerWidth * this.scale);
      this.canvas.height = Math.ceil(window.innerHeight * this.scale);
    },

    setTint(name) {
      this.tint = TINTS[name] || TINTS.lamp;
    },

    pickSprite() {
      let r = Math.random();
      for (let i = 0; i < this.tint.length; i++) {
        if ((r -= this.tint[i]) <= 0) return this.sprites[i];
      }
      return this.sprites[0];
    },

    get limit() {
      return isSmall() ? 170 : 340;
    },

    emit(x, y, opts = {}) {
      if (!this.canvas) return;
      const count = opts.count || 1;
      for (let i = 0; i < count && this.particles.length < this.limit; i++) {
        const life = (opts.life || 2600) * (0.7 + Math.random() * 0.6);
        this.particles.push({
          x: x + (Math.random() - 0.5) * (opts.spread || 8),
          y: y + (Math.random() - 0.5) * (opts.spread || 8),
          vx: (opts.vx || 0) + (Math.random() - 0.5) * 0.5,
          vy: (opts.vy || -0.35) + (Math.random() - 0.5) * 0.3,
          size: (opts.size || 36) * (0.6 + Math.random() * 0.8),
          age: 0,
          life,
          alpha: opts.alpha || 0.16,
          seed: Math.random() * 1000,
          sprite: opts.sprite !== undefined ? this.sprites[opts.sprite] : this.pickSprite(),
        });
      }
      this.wake();
    },

    trail(e) {
      if (!e.target.closest || !e.target.closest('.de-scope')) return;
      const dx = e.movementX || 0;
      const dy = e.movementY || 0;
      const speed = Math.hypot(dx, dy);
      if (speed < 1) return;
      this.emit(e.clientX, e.clientY, {
        count: Math.min(3, Math.ceil(speed / 14)),
        vx: dx * 0.04,
        vy: -0.25 + dy * 0.02,
        size: isSmall() ? 26 : 34,
        life: 1900,
        alpha: 0.14,
      });
    },

    wake() {
      if (this.running || !this.canvas) return;
      this.running = true;
      this.last = performance.now();
      requestAnimationFrame((t) => this.frame(t));
    },

    frame(t) {
      const dt = Math.min(48, t - this.last);
      this.last = t;
      this.emitters.forEach((fn) => fn(dt));

      const { ctx, scale } = this;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.globalCompositeOperation = 'lighter';

      const k = dt / 16.7;
      const alive = [];
      for (const p of this.particles) {
        p.age += dt;
        if (p.age >= p.life) continue;
        // A lazy curl: sideways drift that bends with height and time, plus buoyancy.
        p.vx += Math.sin(p.y * 0.011 + t * 0.0007 + p.seed) * 0.018 * k;
        p.vy -= 0.006 * k;
        p.vx *= Math.pow(0.985, k);
        p.vy *= Math.pow(0.985, k);
        p.x += p.vx * k;
        p.y += p.vy * k;
        p.size *= Math.pow(1.009, k);
        ctx.globalAlpha = p.alpha * Math.sin((Math.PI * p.age) / p.life);
        const s = p.size * scale;
        ctx.drawImage(p.sprite, p.x * scale - s / 2, p.y * scale - s / 2, s, s);
        alive.push(p);
      }
      this.particles = alive;
      ctx.globalAlpha = 1;

      if (this.particles.length || this.emitters.size) {
        requestAnimationFrame((next) => this.frame(next));
      } else {
        this.running = false;
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      }
    },

    addEmitter(fn) {
      this.emitters.add(fn);
      this.wake();
    },
  };

  /* ---------- Reveal on scroll (headings, notes, cards) ---------- */

  const revealer =
    'IntersectionObserver' in window &&
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );

  const observeReveals = (root) => {
    root.querySelectorAll('.de-reveal:not(.is-in)').forEach((el) => {
      if (revealer) revealer.observe(el);
      else el.classList.add('is-in');
    });
  };

  /* ---------- 1–3. The lamp ---------- */

  // Where the middle of the base piece sits, as a fraction of the lamp's height.
  const BASE_CENTRE = 0.87;

  // The lamp's outline inside its picture, as fractions of the picture: where it starts and ends vertically,
  // how far it reaches left of centre beside the finial (its thin tip) and right of centre beside the base,
  // and its widest reach. Measured from the lit picture when the browser allows it; defaults come from the placeholder.
  const DEFAULT_SHAPE = { top: 0.088, bottom: 0.926, tipLeft: 0.038, baseRight: 0.235, width: 0.5 };

  const measureShape = (img) => {
    try {
      const W = 160;
      const H = Math.round((W * img.naturalHeight) / img.naturalWidth);
      const c = document.createElement('canvas');
      c.width = W;
      c.height = H;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, W, H);
      const data = ctx.getImageData(0, 0, W, H).data;
      const solid = (x, y) => data[(y * W + x) * 4 + 3] > 40;
      const rows = [];
      for (let y = 0; y < H; y++) {
        let min = -1;
        let max = -1;
        for (let x = 0; x < W; x++) {
          if (!solid(x, y)) continue;
          if (min < 0) min = x;
          max = x;
        }
        rows.push(min < 0 ? null : [min, max]);
      }
      const filled = rows.map((r, y) => (r ? y : -1)).filter((y) => y >= 0);
      if (filled.length < 10) return DEFAULT_SHAPE;
      const top = filled[0];
      const bottom = filled[filled.length - 1];
      const band = (from, to, fn) => {
        let best = 0;
        for (let y = Math.round(top + (bottom - top) * from); y <= top + (bottom - top) * to; y++) {
          if (rows[y]) best = Math.max(best, fn(rows[y]));
        }
        return best / W;
      };
      return {
        top: top / H,
        bottom: bottom / H,
        tipLeft: band(0, 0.12, ([min]) => W / 2 - min),
        baseRight: band(0.75, 1, ([, max]) => max - W / 2),
        width: band(0, 1, ([min, max]) => max - min),
      };
    } catch (e) {
      // The picture comes from another domain without permission to read it: use the placeholder's outline.
      return DEFAULT_SHAPE;
    }
  };

  // Load a picture so its pixels can be read if the server allows it; otherwise load it plainly (display only).
  const loadReadable = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = () => {
        const plain = new Image();
        plain.onload = () => resolve(plain);
        plain.onerror = () => resolve(null);
        plain.src = src;
      };
      img.src = src;
    });

  // Wrap every word in its own span (keeping italics) so words can form out of the smoke one by one.
  const splitWords = (el) => {
    const out = [];
    el.childNodes.forEach((node) => {
      const isText = node.nodeType === Node.TEXT_NODE;
      const tag = isText ? null : node.tagName.toLowerCase();
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) return out.push(' ');
        const safe = part.replace(/&/g, '&amp;').replace(/</g, '&lt;');
        out.push(`<span class="de-w">${tag ? `<${tag}>${safe}</${tag}>` : safe}</span>`);
      });
    });
    el.innerHTML = out.join('');
    return el.querySelectorAll('.de-w');
  };

  const WORD_HIDDEN = { opacity: 0, yPercent: 60, filter: 'blur(12px)' };
  const WORD_SHOWN = { opacity: 1, yPercent: 0, filter: 'blur(0px)' };

  class Lamp {
    constructor(el) {
      this.el = el;
      this.stage = el.querySelector('.de-lamp__stage');
      this.figure = el.querySelector('[data-de-figure]');
      this.rub = el.querySelector('[data-de-rub]');
      this.reveal = el.querySelector('[data-de-reveal]');
      this.cta = el.querySelector('[data-de-cta]');
      this.canvas = this.rub.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.mask = document.createElement('canvas');
      this.maskCtx = this.mask.getContext('2d');
      this.spout = { x: parseFloat(el.dataset.spoutX) || 0.26, y: parseFloat(el.dataset.spoutY) || 0.36 };
      this.rubbed = 0;
      this.lit = false;
      this.rubbable = false;
      this.visible = false;
      this.progress = 0;
      this.shape = DEFAULT_SHAPE;
      this.build = ['pieces', 'blur'].includes(el.dataset.build) ? el.dataset.build : 'reveal';
      this.litImage = null;

      el.querySelectorAll('[data-de-words]').forEach(splitWords);
      this.chars = this.splitTitle();
      this.loadLit();
      this.bindRub();
      this.watchVisibility();
      if (this.cta) magnetic(this.cta);
      window.addEventListener('resize', () => {
        this.sizeCanvas();
        this.fitLamp();
      });
      if (document.fonts) document.fonts.ready.then(() => this.fitLamp());

      if (hasGsap()) this.buildTimeline();
      else this.staticMode();
    }

    splitTitle() {
      const title = this.el.querySelector('[data-de-title]');
      const text = title.textContent.trim();
      title.setAttribute('aria-label', text);
      title.innerHTML = text
        .split(/\s+/)
        .map((word) => `<span class="de-word" aria-hidden="true">${[...word].map((ch) => `<span class="de-char">${ch}</span>`).join('')}</span>`)
        .join(' ');
      return title.querySelectorAll('.de-char');
    }

    // Work out where the lit lamp should settle. Phones: centred in the slot under its name, as big as fits.
    // Desktop: it keeps at least the size it had while being built — standing on the button and rising behind
    // the two lines — and only shrinks if it would otherwise reach the name.
    fitLamp() {
      this.fitLines();
      const slot = this.el.querySelector('[data-de-slot]');
      const f = this.figure;
      const w = f.offsetWidth;
      const h = f.offsetHeight;
      if (!slot || !w) return;
      const s = slot.getBoundingClientRect();
      const stage = this.stage.getBoundingClientRect();
      if (!s.height) return;
      const { top, bottom, width } = this.shape;
      const tall = bottom - top;
      const fit = Math.min(s.height / (h * tall), s.width / (w * width));
      const slotBottom = s.bottom - stage.top;
      let scale;
      let visibleCentre;
      if (isSmall()) {
        scale = Math.min(fit, 1.3);
        visibleCentre = s.top - stage.top + s.height / 2;
      } else {
        const crown = this.el.querySelector('.de-lamp__crown').getBoundingClientRect().bottom - stage.top;
        scale = Math.min(Math.max(fit, 1), (slotBottom - crown - 6) / (h * tall), 1.3);
        visibleCentre = slotBottom - (h * tall * scale) / 2;
      }
      const cx = s.left - stage.left + s.width / 2;
      const cy = visibleCentre - ((top + bottom) / 2 - 0.5) * h * scale;
      // offsetLeft/offsetTop are the figure's place before any translate or scale is applied.
      f.style.setProperty('--de-lit-x', `${(cx - (f.offsetLeft + w / 2)).toFixed(1)}px`);
      f.style.setProperty('--de-lit-y', `${(cy - (f.offsetTop + h / 2)).toFixed(1)}px`);
      f.style.setProperty('--de-lit-scale', scale.toFixed(3));
      this.litBox = { cx, cy, w: w * scale, h: h * scale };
      this.partLine();
    }

    // Desktop: the two lines stack, centred, between the name and the lamp, sized as a step down from the name.
    fitLines() {
      const lines = [...this.el.querySelectorAll('.de-lamp__lit-lines p')];
      lines.forEach((p) => {
        p.querySelector('.de-gap')?.remove();
        p.style.translate = '';
      });
      if (isSmall() || !lines.length) {
        this.reveal.style.removeProperty('--de-lines-size');
        return;
      }
      const stageWidth = this.stage.offsetWidth;
      const pad = Math.max(24, stageWidth * 0.04);
      const widest = Math.max(
        ...lines.map((p) => {
          p.style.fontSize = '100px';
          const px = p.getBoundingClientRect().width;
          p.style.fontSize = '';
          return px;
        })
      );
      const titleSize = parseFloat(getComputedStyle(this.el.querySelector('[data-de-title]')).fontSize) || 90;
      const size = Math.max(32, Math.min(titleSize * 0.64, ((stageWidth - pad * 2) / widest) * 100));
      this.reveal.style.setProperty('--de-lines-size', `${size.toFixed(1)}px`);
    }

    // Desktop: wherever the lamp rises behind a line wider than its slim tip, that line parts around the lamp
    // (between the two words that leave its halves most even) so no word is hidden behind the bottle.
    partLine() {
      if (isSmall() || !this.litBox) return;
      const { cx, w, h, cy } = this.litBox;
      const { top, bottom, width } = this.shape;
      const stage = this.stage.getBoundingClientRect();
      const tipBottom = cy - h / 2 + (top + (bottom - top) * 0.12) * h;
      const opening = width * w + Math.max(22, stage.width * 0.016) * 2;
      this.el.querySelectorAll('.de-lamp__lit-lines p').forEach((p) => {
        const r = p.getBoundingClientRect();
        if (r.bottom - stage.top <= tipBottom) return; // only the slim tip is behind it: leave it whole
        const words = [...p.querySelectorAll('.de-w')];
        if (words.length < 2) return;
        const full = p.offsetWidth;
        let split = 1;
        let best = Infinity;
        for (let i = 1; i < words.length; i++) {
          const diff = Math.abs(words[i].offsetLeft - (full - words[i].offsetLeft));
          if (diff < best) [best, split] = [diff, i];
        }
        const spacer = document.createElement('span');
        spacer.className = 'de-gap';
        spacer.setAttribute('aria-hidden', 'true');
        spacer.style.cssText = `display:inline-block;width:${opening.toFixed(1)}px`;
        words[split].before(spacer);
        const box = p.getBoundingClientRect();
        const gapCentre = box.left - stage.left + spacer.offsetLeft + opening / 2;
        p.style.translate = `${(cx - gapCentre).toFixed(1)}px 0`;
      });
    }

    loadLit() {
      loadReadable(this.rub.dataset.litSrc).then((img) => {
        if (!img) return;
        this.litImage = img;
        this.shape = measureShape(img);
        this.sizeCanvas();
        this.fitLamp();
        if (hasGsap()) window.ScrollTrigger.refresh();
      });
    }

    sizeCanvas() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = this.figure.offsetWidth;
      const height = this.figure.offsetHeight;
      if (!width) return;
      const w = Math.round(width * dpr);
      const h = Math.round(height * dpr);
      if (this.canvas.width === w && this.canvas.height === h) return;
      // Keep what has been rubbed so far when the size changes.
      const old = this.mask.width ? this.mask : null;
      const copy = old && document.createElement('canvas');
      if (copy) {
        copy.width = old.width;
        copy.height = old.height;
        copy.getContext('2d').drawImage(old, 0, 0);
      }
      this.canvas.width = this.mask.width = w;
      this.canvas.height = this.mask.height = h;
      if (copy) this.maskCtx.drawImage(copy, 0, 0, w, h);
      this.draw();
    }

    draw() {
      const { ctx, canvas } = this;
      if (!this.litImage || !canvas.width) return;
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(this.mask, 0, 0);
      ctx.globalCompositeOperation = 'source-in';
      ctx.drawImage(this.litImage, 0, 0, canvas.width, canvas.height);
    }

    stamp(x, y) {
      const { maskCtx, mask } = this;
      const r = mask.width * 0.2;
      const g = maskCtx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(255,255,255,0.4)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      maskCtx.fillStyle = g;
      maskCtx.fillRect(x - r, y - r, r * 2, r * 2);
    }

    bindRub() {
      let prev = null;
      const onMove = (e) => {
        if (!this.rubbable || this.lit) return;
        const rect = this.canvas.getBoundingClientRect();
        const sx = this.canvas.width / rect.width;
        const x = (e.clientX - rect.left) * sx;
        const y = (e.clientY - rect.top) * sx;
        if (prev) {
          const dist = Math.hypot(x - prev.x, y - prev.y);
          const steps = Math.max(1, Math.ceil(dist / (this.mask.width * 0.05)));
          for (let i = 1; i <= steps; i++) {
            this.stamp(prev.x + ((x - prev.x) * i) / steps, prev.y + ((y - prev.y) * i) / steps);
          }
          this.rubbed += dist / this.canvas.width;
          this.el.style.setProperty('--de-glow', Math.min(0.6, this.rubbed * 0.25).toFixed(3));
          this.draw();
          Smoke.emit(e.clientX, e.clientY, { count: 2, size: 40, alpha: 0.18, vy: -0.6 });
          if (this.rubbed > 2.2) this.light();
        }
        prev = { x, y };
      };
      const reset = () => (prev = null);
      this.rub.addEventListener('pointermove', onMove);
      this.rub.addEventListener('pointerleave', reset);
      this.rub.addEventListener('pointerup', reset);
      this.rub.addEventListener('pointercancel', reset);
      this.rub.addEventListener('click', () => this.rubbable && this.light());
      this.rub.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (this.rubbable) this.light();
        }
      });
    }

    setRubbable(on) {
      if (this.rubbable === on) return;
      this.rubbable = on;
      this.rub.tabIndex = on && !this.lit ? 0 : -1;
      if (on) this.sizeCanvas();
    }

    light() {
      if (this.lit) return;
      this.lit = true;
      this.rub.tabIndex = -1;
      this.rub.setAttribute('aria-disabled', 'true');
      if (this.cta) this.cta.removeAttribute('tabindex');
      this.fitLamp();
      this.el.classList.add('is-lit');
      this.el.style.setProperty('--de-glow', '1');

      // Let the light flood the rest of the lamp.
      const start = performance.now();
      const flood = (t) => {
        const k = Math.min(1, (t - start) / 1300);
        this.maskCtx.fillStyle = `rgba(255,255,255,${0.08 + k * 0.3})`;
        this.maskCtx.fillRect(0, 0, this.mask.width, this.mask.height);
        this.draw();
        if (k < 1) requestAnimationFrame(flood);
      };
      requestAnimationFrame(flood);

      const tip = this.spoutPoint();
      Smoke.emit(tip.x, tip.y, { count: 40, spread: 30, size: 60, life: 3600, alpha: 0.2, vx: -0.4, vy: -1.1 });
      this.startSpout();

      this.el.dispatchEvent(new CustomEvent('de:lit', { bubbles: true }));
      if (window.gtag) window.gtag('event', 'dallah_lamp_lit');
    }

    spoutPoint() {
      const rect = this.figure.getBoundingClientRect();
      return { x: rect.left + rect.width * this.spout.x, y: rect.top + rect.height * this.spout.y };
    }

    startSpout() {
      if (this.spoutEmitter) return;
      let acc = 0;
      this.spoutEmitter = (dt) => {
        if (!this.visible || !this.lit) return;
        acc += dt;
        if (acc < 70) return;
        acc = 0;
        const tip = this.spoutPoint();
        Smoke.emit(tip.x, tip.y, { count: 1, spread: 6, size: 30, life: 3400, alpha: 0.14, vx: -0.3, vy: -0.7 });
      };
      Smoke.addEmitter(this.spoutEmitter);
    }

    // Faint smoke drifting around the base while the lamp is being built.
    startAmbient() {
      let acc = 0;
      Smoke.addEmitter((dt) => {
        if (!this.visible || this.lit || this.progress > 0.6) return;
        acc += dt;
        if (acc < 200) return;
        acc = 0;
        const base = (this.el.querySelector('[data-piece="base"]') || this.figure).getBoundingClientRect();
        Smoke.emit(base.left + base.width * (0.25 + Math.random() * 0.5), base.top + base.height * (0.78 + Math.random() * 0.14), {
          size: 70,
          life: 4200,
          alpha: 0.08,
          vy: -0.25,
        });
      });
    }

    watchVisibility() {
      new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) Smoke.wake();
      }).observe(this.el);
    }

    buildTimeline() {
      const { gsap } = window;
      const q = (s) => this.el.querySelector(s);
      const piece = (name) => q(`[data-piece="${name}"]`);
      const line = (name) => q(`[data-de-line="${name}"]`);
      const pieces = this.build === 'pieces';
      const lampLayers = this.el.querySelectorAll('.de-piece, .de-lamp__fill');

      if (pieces) {
        // The base starts alone in the middle of the screen; the other pieces wait, hidden, just above their places.
        const toScreenCentre = () => {
          const f = this.figure.getBoundingClientRect();
          const s = this.stage.getBoundingClientRect();
          return {
            x: s.left + s.width / 2 - (f.left + f.width / 2),
            y: s.top + s.height / 2 - (f.top + f.height * BASE_CENTRE),
          };
        };
        gsap.set(piece('base'), { x: () => toScreenCentre().x, y: () => toScreenCentre().y, scale: 1.35 });
        gsap.set(['body', 'neck', 'lid'].map(piece), { autoAlpha: 0, y: () => -0.2 * this.figure.offsetHeight });
      }

      // The opening line forms as soon as the page loads.
      const intro = line('intro');
      gsap.set(intro, { autoAlpha: 1 });
      gsap.from(intro.querySelector('.de-line__lead'), { opacity: 0, x: -20, duration: 1, delay: 0.3 });
      gsap.from(intro.querySelector('.de-rule'), { scaleX: 0, duration: 1.2, delay: 0.3, ease: 'power3.out' });
      gsap.from(intro.querySelectorAll('.de-w'), { ...WORD_HIDDEN, duration: 1.1, stagger: 0.12, delay: 0.6, ease: 'power3.out' });

      const tl = gsap.timeline({ defaults: { ease: 'power2.inOut' } });

      const showLine = (name, at) => {
        const el = line(name);
        tl.set(el, { autoAlpha: 1, y: 0, filter: 'blur(0px)' }, at);
        const lead = el.querySelector('.de-line__lead');
        if (lead) {
          tl.fromTo(lead, { opacity: 0, x: -24 }, { opacity: 1, x: 0, duration: 0.5 }, at);
          tl.fromTo(lead.querySelector('.de-rule'), { scaleX: 0 }, { scaleX: 1, duration: 0.6 }, at);
        }
        tl.fromTo(el.querySelectorAll('.de-w'), WORD_HIDDEN, { ...WORD_SHOWN, duration: 0.5, stagger: 0.07, ease: 'power3.out' }, at + 0.1);
      };
      const hideLine = (name, at) =>
        tl.to(line(name), { autoAlpha: 0, y: -30, filter: 'blur(10px)', duration: 0.5, ease: 'power2.in' }, at);
      const arrive = (el, at, duration = 2) => {
        tl.to(el, { autoAlpha: 1, duration: 0.8, ease: 'none' }, at);
        tl.to(el, { y: 0, duration }, at);
      };
      tl.to(q('[data-de-cue]'), { autoAlpha: 0, duration: 0.4 }, 0.3);
      hideLine('intro', 1.0);

      // Reveal build (default): the opening line, then "The dallah is ready…", then the dark dallah rises into the
      // centre, waiting to be rubbed. The other builds show the dallah building up before the same moment.
      const revealing = this.build === 'reveal';
      const wholeAt = revealing ? 1.5 : 8.35;
      const rubAt = revealing ? 3.4 : 8.2;

      if (pieces) {
        tl.to(piece('base'), { x: 0, y: 0, scale: 1, '--de-scatter': 0, duration: 2 }, 1.2);
        arrive(piece('body'), 3.4);
        arrive(piece('neck'), 3.9, 2.1);
        arrive(piece('lid'), 5.9);
      } else if (!revealing) {
        // The whole dallah starts blurred, faint and slightly large, and comes into focus in three steps.
        const lamp = q('.de-lamp__fill');
        tl.fromTo(
          lamp,
          { '--de-blur': '14px', '--de-dim': 0.56, opacity: 0.85, scale: 1.04 },
          { '--de-blur': '8px', '--de-dim': 0.58, opacity: 0.92, scale: 1.03, duration: 2 },
          1.2
        );
        tl.to(lamp, { '--de-blur': '3px', '--de-dim': 0.6, opacity: 0.97, scale: 1.015, duration: 2.3 }, 3.4);
        tl.to(lamp, { '--de-blur': '0px', '--de-dim': 0.62, opacity: 1, scale: 1, duration: 1.8 }, 5.9);
      }

      showLine('whole', wholeAt);
      if (revealing) {
        // The dallah waits softly blurred behind the words; when they're done it sharpens and darkens into the
        // dark dallah that is rubbed, like a camera finding focus.
        const blurred = q('.de-lamp__fill');
        hideLine('whole', rubAt - 0.4);
        if (blurred) {
          tl.fromTo(
            blurred,
            { '--de-blur': '12px', '--de-dim': 0.5, opacity: 0.75, scale: 1.04 },
            { '--de-blur': '0px', '--de-dim': 0.22, opacity: 1, scale: 1, duration: 1, ease: 'power2.inOut' },
            rubAt - 0.6
          );
          tl.to(this.rub, { autoAlpha: 1, duration: 0.5 }, rubAt + 0.3);
          tl.to(blurred, { autoAlpha: 0, duration: 0.3 }, rubAt + 0.8);
        } else {
          tl.fromTo(this.rub, { autoAlpha: 0, scale: 0.9 }, { autoAlpha: 1, scale: 1, duration: 0.9, ease: 'power2.out' }, rubAt);
        }
      } else {
        tl.to(lampLayers, { '--de-dim': 0.22, duration: 0.6 }, 7.6);
        tl.to(this.rub, { autoAlpha: 1, duration: 0.5 }, rubAt);
        tl.to(lampLayers, { autoAlpha: 0, duration: 0.3 }, 8.5);
      }
      tl.addLabel('rub', rubAt);
      tl.fromTo(q('[data-de-hint]'), { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.4 }, rubAt + 0.9);
      tl.addLabel('autolight', rubAt + 3); // anyone who scrolls straight past the rubbing still sees the lamp light here…
      tl.to({}, { duration: 2.6 }, rubAt + 3); // …and this stretch keeps the lit lamp on screen while its name appears

      const at = (label) => tl.labels[label] / tl.duration();
      gsap.registerPlugin(window.ScrollTrigger);
      window.ScrollTrigger.create({
        animation: tl,
        trigger: this.el,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.8,
        invalidateOnRefresh: true,
        onUpdate: (st) => {
          this.progress = st.progress;
          this.setRubbable(st.progress > at('rub'));
          if (st.progress > at('autolight') && !this.lit) this.light();
          // Scrolling back up to the building stage hides the name so it never sits on top of the lines.
          this.el.classList.toggle('is-rewound', this.lit && st.progress < at('rub'));
        },
      });

      // Once lit: the lamp glides to the centre (CSS), its name forms above it, then the two lines and the button.
      const reveal = gsap.timeline({ paused: true, defaults: { ease: 'power3.out' } });
      const eyebrow = q('[data-de-eyebrow]');
      reveal.set(this.reveal, { autoAlpha: 1 });
      if (eyebrow) {
        reveal.from(eyebrow, { opacity: 0, y: 12, duration: 0.8 }, 0.5);
        reveal.from(eyebrow.querySelectorAll('.de-rule'), { scaleX: 0, duration: 1 }, 0.5);
      }
      const title = q('[data-de-title]');
      const puff = (ch) => {
        const r = ch.getBoundingClientRect();
        Smoke.emit(r.left + r.width / 2, r.top + r.height * 0.55, {
          count: 6,
          spread: r.width * 1.2,
          size: r.height * 0.9,
          life: 2600,
          alpha: 0.13,
          vy: -0.35,
          sprite: Math.random() < 0.6 ? 2 : 1,
        });
      };
      const middle = (this.chars.length - 1) / 2;
      this.chars.forEach((ch, i) => reveal.call(() => puff(ch), null, 0.55 + Math.abs(i - middle) * 0.07));
      reveal.fromTo(title, { letterSpacing: '0.3em' }, { letterSpacing: '0.08em', duration: 2.4, ease: 'power3.out' }, 0.6);
      reveal.from(
        this.chars,
        { opacity: 0, filter: 'blur(24px)', scale: 1.4, yPercent: 30, duration: 1.7, ease: 'power2.out', stagger: { each: 0.07, from: 'center' } },
        0.65
      );
      const ornament = q('.de-lamp__ornament');
      if (ornament) reveal.from(ornament, { opacity: 0, scaleX: 0, duration: 1.1 }, 1.6);
      this.el.querySelectorAll('.de-lamp__lit-lines p').forEach((p, i) => {
        reveal.from(p.querySelectorAll('.de-w'), { ...WORD_HIDDEN, duration: 0.9, stagger: 0.09 }, 2.1 + i * 0.7);
      });
      if (this.cta) reveal.from(this.cta, { opacity: 0, y: 24, scale: 0.92, duration: 0.9, ease: 'back.out(1.7)' }, 3.3);
      this.el.addEventListener('de:lit', () => reveal.play());

      this.startAmbient();
    }

    staticMode() {
      const q = (s) => this.el.querySelector(s);
      this.el.querySelectorAll('.de-piece, .de-lamp__fill').forEach((p) => (p.style.visibility = 'hidden'));
      [this.rub, q('[data-de-line="whole"]'), q('[data-de-hint]')].forEach((el) => {
        el.style.opacity = 1;
        el.style.visibility = 'visible';
      });
      q('[data-de-cue]').style.opacity = 0;
      this.el.addEventListener('de:lit', () => {
        [q('[data-de-line="whole"]'), q('[data-de-hint]')].forEach((el) => (el.style.visibility = 'hidden'));
        this.reveal.style.opacity = 1;
        this.reveal.style.visibility = 'visible';
      });
      this.progress = 1;
      this.setRubbable(true);
    }
  }

  // The button leans towards the pointer and its inner glow follows it.
  const magnetic = (el) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      el.style.setProperty('--mx', `${x}px`);
      el.style.setProperty('--my', `${y}px`);
      if (!reduceMotion) el.style.translate = `${((x - r.width / 2) / r.width) * 12}px ${((y - r.height / 2) / r.height) * 10}px`;
    });
    el.addEventListener('pointerleave', () => {
      el.style.translate = '';
      el.style.removeProperty('--mx');
      el.style.removeProperty('--my');
    });
  };

  /* ---------- Product reveal ---------- */

  // One line of words over the blurred product; scrolling brings the product into focus. At that moment smoke
  // bursts from the dallah's spout (and keeps rising), and the product's name forms from smoke above it.
  const initProduct = (el) => {
    const q = (sel) => el.querySelector(sel);
    const figure = q('[data-de-figure]');
    const crown = q('[data-de-crown]');
    const title = q('[data-de-title]');
    const intro = q('[data-de-line="intro"]');
    const cue = q('[data-de-cue]');
    const shop = q('[data-de-shop]');
    const spout = { x: parseFloat(el.dataset.spoutX) || 0.38, y: parseFloat(el.dataset.spoutY) || 0.29 };
    let shown = false;
    let visible = false;

    el.querySelectorAll('[data-de-words]').forEach(splitWords);
    let chars = [];
    if (title) {
      const text = title.textContent.trim();
      title.setAttribute('aria-label', text);
      title.innerHTML = text
        .split(/\s+/)
        .map((word) => `<span class="de-word" aria-hidden="true">${[...word].map((ch) => `<span class="de-char">${ch}</span>`).join('')}</span>`)
        .join(' ');
      chars = [...title.querySelectorAll('.de-char')];
    }

    if (!hasGsap()) {
      // No animation: the product simply stands in full view with its name.
      figure.style.filter = 'none';
      figure.style.opacity = 1;
      figure.style.scale = 1;
      crown.style.opacity = 1;
      crown.style.visibility = 'visible';
      cue.style.display = 'none';
      if (shop) {
        shop.style.opacity = 1;
        shop.style.visibility = 'visible';
        shop.removeAttribute('tabindex');
      }
      intro.style.display = 'none';
      return;
    }
    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);

    new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) Smoke.wake();
    }).observe(el);

    const spoutPoint = () => {
      const r = figure.getBoundingClientRect();
      return { x: r.left + r.width * spout.x, y: r.top + r.height * spout.y };
    };

    // The opening line forms as soon as the page loads.
    gsap.set(intro, { autoAlpha: 1 });
    const lead = intro.querySelector('.de-line__lead');
    if (lead) {
      gsap.from(lead, { opacity: 0, y: 10, duration: 1, delay: 0.3 });
      gsap.from(lead.querySelectorAll('.de-rule'), { scaleX: 0, duration: 1.2, delay: 0.3, ease: 'power3.out' });
    }
    gsap.from(intro.querySelectorAll('.de-w'), { ...WORD_HIDDEN, duration: 1.1, stagger: 0.12, delay: 0.6, ease: 'power3.out' });

    // The name, formed from smoke: letters condense from the centre outwards as puffs rise where each appears.
    const naming = gsap.timeline({ paused: true, defaults: { ease: 'power3.out' } });
    naming.set(crown, { autoAlpha: 1 });
    const eyebrow = q('[data-de-eyebrow]');
    if (eyebrow) {
      naming.from(eyebrow, { opacity: 0, y: 12, duration: 0.8 }, 0.3);
      naming.from(eyebrow.querySelectorAll('.de-rule'), { scaleX: 0, duration: 1 }, 0.3);
    }
    if (chars.length) {
      const puff = (ch) => {
        const r = ch.getBoundingClientRect();
        Smoke.emit(r.left + r.width / 2, r.top + r.height * 0.55, {
          count: 6,
          spread: r.width * 1.2,
          size: r.height * 0.9,
          life: 2600,
          alpha: 0.13,
          vy: -0.35,
          sprite: Math.random() < 0.6 ? 2 : 1,
        });
      };
      const middle = (chars.length - 1) / 2;
      chars.forEach((ch, i) => naming.call(() => puff(ch), null, 0.45 + Math.abs(i - middle) * 0.07));
      naming.fromTo(title, { letterSpacing: '0.3em' }, { letterSpacing: '0.08em', duration: 2.4 }, 0.5);
      naming.from(
        chars,
        { opacity: 0, filter: 'blur(24px)', scale: 1.4, yPercent: 30, duration: 1.7, ease: 'power2.out', stagger: { each: 0.07, from: 'center' } },
        0.55
      );
      const ornament = q('.de-lamp__ornament');
      if (ornament) naming.from(ornament, { opacity: 0, scaleX: 0, duration: 1.1 }, 1.5);
    }

    if (shop) {
      naming.fromTo(shop, { autoAlpha: 0, y: 24, scale: 0.92 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.9, ease: 'back.out(1.7)' }, chars.length ? 2.1 : 0.6);
      magnetic(shop);
    }

    const show = () => {
      if (shown) return;
      shown = true;
      el.classList.add('is-lit');
      if (shop) shop.removeAttribute('tabindex');
      const tip = spoutPoint();
      Smoke.emit(tip.x, tip.y, { count: 22, spread: 46, size: 64, life: 3600, alpha: 0.11, vx: -0.4, vy: -1 });
      naming.play();
      if (window.gtag) window.gtag('event', 'dallah_product_revealed');
    };

    // Smoke: a faint haze around the product while it is blurred, then a steady thread from the spout.
    let haze = 0;
    let thread = 0;
    Smoke.addEmitter((dt) => {
      if (!visible) return;
      if (!shown) {
        haze += dt;
        if (haze < 200) return;
        haze = 0;
        const r = figure.getBoundingClientRect();
        Smoke.emit(r.left + r.width * (0.3 + Math.random() * 0.4), r.top + r.height * (0.55 + Math.random() * 0.3), { size: 70, life: 4200, alpha: 0.08, vy: -0.25 });
        return;
      }
      if (el.classList.contains('is-rewound')) return;
      thread += dt;
      if (thread < 70) return;
      thread = 0;
      const tip = spoutPoint();
      Smoke.emit(tip.x, tip.y, { count: 1, spread: 6, size: 30, life: 3400, alpha: 0.14, vx: -0.3, vy: -0.7 });
    });

    const tl = gsap.timeline({ defaults: { ease: 'power2.inOut' } });
    tl.to(cue, { autoAlpha: 0, duration: 0.4 }, 0.3);
    tl.to(intro, { autoAlpha: 0, y: -30, filter: 'blur(10px)', duration: 0.5, ease: 'power2.in' }, 1.0);
    tl.to(figure, { '--de-blur': '0px', '--de-dim': 1, opacity: 1, scale: 1, duration: 1.5 }, 1.0);
    tl.addLabel('reveal', 2.0);
    tl.to({}, { duration: 2.5 }, 2.5); // the revealed product stays on screen while its name forms

    ScrollTrigger.create({
      animation: tl,
      trigger: el,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.8,
      onUpdate: (st) => {
        const past = st.progress > tl.labels.reveal / tl.duration();
        if (past) show();
        // Scrolling back up to the opening line hides the name again.
        el.classList.toggle('is-rewound', shown && !past);
      },
    });
  };

  /* ---------- The heritage ---------- */

  // A bead travels down the gold thread with the scroll; each chapter lights when the bead reaches its star.
  const initHeritage = (el) => {
    const list = el.querySelector('[data-de-heritage-list]');
    const chapters = [...el.querySelectorAll('.de-chapter')];
    if (!hasGsap() || !list || !chapters.length) return; // no animation: the whole thread is gold and every chapter shows
    el.classList.add('is-live');
    window.gsap.registerPlugin(window.ScrollTrigger);
    window.ScrollTrigger.create({
      trigger: list,
      start: 'top 62%',
      end: 'bottom 62%',
      scrub: 0.6,
      onUpdate: (st) => {
        el.style.setProperty('--de-thread', st.progress.toFixed(4));
        const reached = st.progress * list.offsetHeight;
        // offsetTop is each chapter's place in the list; its star sits at its top.
        chapters.forEach((chapter) => chapter.classList.toggle('is-on', chapter.offsetTop <= reached + 24));
      },
    });
  };

  /* ---------- The story: collage ---------- */

  // The pictures start as a pile of photographs in the middle; scrolling fans them out to their places.
  const initCollage = (el) => {
    const grid = el.querySelector('[data-de-collage-grid]');
    const tiles = [...el.querySelectorAll('.de-tile')];
    if (!hasGsap() || !grid || !tiles.length) return; // no animation: the finished collage sits in the page
    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);
    el.classList.add('is-live');

    const lean = [-13, 9, -7, 12, -10];
    const images = tiles.map((tile) => tile.querySelector('img'));
    const captions = tiles.map((tile) => tile.querySelector('.de-tile__caption'));
    const glide = gsap.parseEase('power3.inOut');
    const settle = gsap.parseEase('power2.out');
    // Timing, in the same units: a short lead-in, each picture takes TRAVEL to reach its place and starts GAP
    // after the one before, then the finished collage holds for a moment.
    const LEAD = 0.15;
    const TRAVEL = 2;
    const GAP = 0.14;
    const HOLD = 1.1;
    const total = LEAD + TRAVEL + GAP * (tiles.length - 1) + HOLD;
    const state = { progress: 0 };
    let pile = [];

    // Where each picture sits in the pile, relative to its final place (offsetLeft/offsetTop ignore transforms).
    const measure = () => {
      pile = tiles.map((tile, i) => ({
        x: grid.offsetWidth / 2 - (tile.offsetLeft + tile.offsetWidth / 2),
        y: grid.offsetHeight / 2 - (tile.offsetTop + tile.offsetHeight / 2),
        turn: lean[i % lean.length],
        scale: Math.min(0.7, (grid.offsetHeight * 0.62) / tile.offsetHeight, (grid.offsetWidth * 0.6) / tile.offsetWidth),
      }));
    };

    const render = () => {
      const time = state.progress * total;
      tiles.forEach((tile, i) => {
        const local = Math.min(1, Math.max(0, (time - LEAD - i * GAP) / TRAVEL));
        const away = 1 - glide(local); // 1 = in the pile, 0 = in place
        const from = pile[i];
        tile.style.transform = `translate3d(${(from.x * away).toFixed(1)}px, ${(from.y * away).toFixed(1)}px, 0) rotate(${(from.turn * away).toFixed(2)}deg) scale(${(1 + (from.scale - 1) * away).toFixed(4)})`;
        if (images[i]) images[i].style.transform = `scale(${(1 + 0.3 * (1 - settle(local))).toFixed(4)})`;
        // Phones show the captions under the pictures: fade each in as its picture arrives, so they never pile up.
        if (captions[i]) captions[i].style.opacity = isSmall() ? Math.max(0, (local - 0.6) / 0.4).toFixed(2) : '';
      });
    };

    gsap.to(state, {
      progress: 1,
      ease: 'none',
      onUpdate: render,
      scrollTrigger: {
        trigger: el,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1,
        onRefresh: () => {
          measure();
          render();
        },
      },
    });
    measure();
    render();
  };

  /* ---------- The collection ---------- */

  // Two curtains part as the picture scrolls into view, then each fragrance's name appears under its bottle.
  // On a computer the light follows the pointer to whichever bottle is nearest.
  const initRange = (el) => {
    const frame = el.querySelector('[data-de-range-frame]');
    if (!frame) return;
    const names = [...frame.querySelectorAll('.de-range__names li')];
    const cta = el.querySelector('[data-de-range-cta]');
    if (cta) magnetic(cta);

    const light = (li) => {
      names.forEach((n) => n.classList.toggle('is-active', n === li));
      frame.classList.toggle('is-lit', Boolean(li));
      if (li) frame.style.setProperty('--de-sx', `${li.dataset.x}%`);
    };
    if (names.length && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      frame.addEventListener('pointermove', (e) => {
        const r = frame.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 100;
        light(names.reduce((best, n) => (Math.abs(n.dataset.x - x) < Math.abs(best.dataset.x - x) ? n : best)));
      });
      frame.addEventListener('pointerleave', () => light(null));
    }
    names.forEach((n) => {
      n.addEventListener('focusin', () => light(n));
      n.addEventListener('focusout', () => light(null));
    });

    if (!hasGsap()) return; // no animation: the picture and names simply show
    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);
    el.classList.add('is-live');
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: frame,
        start: 'top 85%',
        end: 'bottom 92%', // fully open as soon as the whole picture is on screen, on phones as well as computers
        scrub: 0.8,
        onUpdate: (st) => el.classList.toggle('is-open', st.progress > 0.8),
      },
    });
    tl.to(frame.querySelector('.de-range__curtain--left'), { xPercent: -102, ease: 'power2.inOut', duration: 1 }, 0);
    tl.to(frame.querySelector('.de-range__curtain--right'), { xPercent: 102, ease: 'power2.inOut', duration: 1 }, 0);
    tl.fromTo(frame.querySelector('img'), { scale: 1.18 }, { scale: 1, ease: 'power2.out', duration: 1 }, 0);
  };

  /* ---------- The film ---------- */

  // A plain video block that plays by itself, silently, in a loop. A Shopify video plays while it is on screen and
  // rests while it is not; a YouTube / Vimeo one is loaded as the section comes near. The cover picture stays in
  // front until the video is actually running.
  const initFilm = (el) => {
    const stage = el.querySelector('[data-de-film-stage]');
    // With a second video for phones, keep only the one for this screen (the other would download for nothing).
    const phoneVideo = el.querySelector('.de-film__video--phone');
    if (phoneVideo) {
      const wideVideo = el.querySelector('.de-film__video--wide');
      (window.matchMedia('(max-width: 749px)').matches ? wideVideo : phoneVideo).remove();
    }
    const video = el.querySelector('.de-film__video');
    const embed = el.querySelector('[data-de-film-embed]');
    if (!stage || (!video && !embed)) return; // no video chosen yet: the "Coming soon" label shows instead
    const started = () => el.classList.add('is-playing');

    if (video) {
      video.muted = true; // browsers only allow a video to start by itself when it is silent
      video.addEventListener('playing', started);
      if (!video.paused) started();
    }
    const loadEmbed = () => {
      if (!embed || embed.firstChild) return;
      const id = encodeURIComponent(embed.dataset.id);
      const src =
        embed.dataset.host === 'vimeo'
          ? `https://player.vimeo.com/video/${id}?background=1&autoplay=1&loop=1&muted=1`
          : `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&rel=0&playsinline=1&modestbranding=1`;
      const frame = document.createElement('iframe');
      frame.src = src;
      frame.title = embed.dataset.title || '';
      frame.allow = 'autoplay; encrypted-media; picture-in-picture';
      frame.addEventListener('load', () => setTimeout(started, 900)); // give the player a moment to begin
      embed.appendChild(frame);
    };

    if (!('IntersectionObserver' in window)) {
      loadEmbed();
      if (video) video.play().catch(() => {});
      return;
    }
    new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadEmbed();
          if (video && video.paused) video.play().catch(() => {});
        } else if (video && !video.paused) {
          video.pause();
        }
      },
      { rootMargin: '300px 0px' }
    ).observe(stage);
  };

  /* ---------- The offering: buy section ---------- */

  // Size options, quantity, "Buy it now", the tilting dallah and its smoke. Adding to the bag itself is done by the
  // theme's own product form (it opens the cart drawer); this only adds the "Added" moment on top.
  const initBuy = (el) => {
    const form = el.querySelector('form');
    if (!form) return;
    const idInput = form.querySelector('[name="id"]');
    const qty = form.querySelector('[data-de-qty-input]');
    const add = form.querySelector('[data-de-add]');
    const label = add.querySelector('.de-cta__label');
    const buyNow = form.querySelector('[data-de-buy-now]');
    const price = el.querySelector('[data-de-price]');
    const compare = el.querySelector('[data-de-compare]');
    const save = el.querySelector('[data-de-save]');
    const quantity = () => Math.max(1, parseInt(qty.value, 10) || 1);
    let available = add.getAttribute('aria-disabled') !== 'true';

    // Product pictures: a thumbnail shows its photograph as a medallion inside the ring; the first thumbnail
    // brings back the floating dallah.
    const bottleBox = el.querySelector('[data-de-tilt-target]');
    const photo = el.querySelector('[data-de-photo]');
    const thumbs = [...el.querySelectorAll('[data-de-thumb]')];
    const showPicture = (src, imageId) => {
      const thumb = thumbs.find((t) => (src ? (imageId && t.dataset.imageId === imageId) || t.dataset.src === src : !t.dataset.src));
      thumbs.forEach((t) => {
        t.classList.toggle('is-selected', t === thumb);
        t.setAttribute('aria-pressed', t === thumb ? 'true' : 'false');
      });
      if (!photo || !bottleBox) return;
      if (src) {
        const img = photo.querySelector('img');
        if (img.getAttribute('src') !== src) img.src = src;
        // Restart the little entrance each time a different photograph is chosen.
        photo.hidden = true;
        void photo.offsetWidth;
      }
      photo.hidden = !src;
      bottleBox.hidden = Boolean(src);
    };
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => showPicture(thumb.dataset.src || '', thumb.dataset.imageId)));

    const syncBuyNow = () => {
      if (buyNow) buyNow.href = `${el.dataset.cartUrl}/${idInput.value}:${quantity()}`;
    };

    // Choosing a size updates the price, the sale pill, and whether it can be bought.
    const options = [...form.querySelectorAll('[data-de-variant]')];
    options.forEach((option) => {
      option.addEventListener('click', () => {
        options.forEach((o) => {
          o.classList.toggle('is-selected', o === option);
          o.setAttribute('aria-pressed', o === option ? 'true' : 'false');
        });
        idInput.value = option.dataset.deVariant;
        price.textContent = option.dataset.price;
        const onSale = Boolean(option.dataset.compare);
        compare.hidden = !onSale;
        save.hidden = !onSale;
        if (onSale) {
          compare.textContent = option.dataset.compare;
          save.textContent = `${save.dataset.saveLabel} ${option.dataset.saved}%`;
        }
        available = option.dataset.available === 'true';
        add.toggleAttribute('aria-disabled', !available);
        if (!available) add.setAttribute('aria-disabled', 'true');
        label.textContent = available ? add.dataset.label : add.dataset.soldOut;
        if (buyNow) buyNow.hidden = !available;
        if (option.dataset.imageSrc) showPicture(option.dataset.imageSrc, option.dataset.imageId);
        syncBuyNow();
      });
    });

    form.querySelectorAll('[data-de-qty]').forEach((button) => {
      button.addEventListener('click', () => {
        qty.value = Math.max(1, quantity() + parseInt(button.dataset.deQty, 10));
        syncBuyNow();
      });
    });
    qty.addEventListener('change', () => {
      qty.value = quantity();
      syncBuyNow();
    });

    // The "Added" moment: the theme announces a successful add; answer it only if this form sent it.
    let sending = false;
    let reset;
    form.addEventListener('submit', () => {
      sending = available;
    });
    document.addEventListener('product-ajax:added', () => {
      if (!sending) return;
      sending = false;
      add.classList.add('is-added');
      label.textContent = add.dataset.added;
      const r = add.getBoundingClientRect();
      Smoke.emit(r.left + r.width / 2, r.top + r.height / 2, { count: 26, spread: r.width * 0.6, size: 70, life: 3000, alpha: 0.13, vy: -0.9, sprite: 2 });
      clearTimeout(reset);
      reset = setTimeout(() => {
        add.classList.remove('is-added');
        label.textContent = available ? add.dataset.label : add.dataset.soldOut;
      }, 2600);
    });
    document.addEventListener('product-ajax:error', () => (sending = false));

    // The dallah leans towards the pointer, and a thread of smoke rises from its spout while it is on screen.
    const visual = el.querySelector('[data-de-tilt]');
    const bottle = el.querySelector('[data-de-tilt-target]');
    if (visual && bottle) {
      if (!reduceMotion && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        visual.addEventListener('pointermove', (e) => {
          const r = visual.getBoundingClientRect();
          const x = ((e.clientX - r.left) / r.width - 0.5) * 2;
          const y = ((e.clientY - r.top) / r.height - 0.5) * 2;
          bottle.style.transform = `rotateY(${(x * 13).toFixed(2)}deg) rotateX(${(-y * 10).toFixed(2)}deg) translateZ(24px)`;
        });
        visual.addEventListener('pointerleave', () => (bottle.style.transform = ''));
      }
      const img = bottle.querySelector('img');
      let visible = false;
      let thread = 0;
      new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        if (visible) Smoke.wake();
      }).observe(visual);
      Smoke.addEmitter((dt) => {
        if (!visible || bottle.hidden) return; // no smoke while a photograph is showing instead of the dallah
        thread += dt;
        if (thread < 90) return;
        thread = 0;
        const r = img.getBoundingClientRect();
        Smoke.emit(r.left + r.width * 0.26, r.top + r.height * 0.36, { count: 1, spread: 6, size: 28, life: 3200, alpha: 0.13, vx: -0.3, vy: -0.7 });
      });
    }
  };

  /* ---------- 4. The details: each window slowly zooms out as it passes ---------- */

  const initDetails = (el) => {
    if (!hasGsap()) return;
    el.querySelectorAll('[data-de-zoom]').forEach((img) => {
      window.gsap.fromTo(
        img,
        { scale: 1.4 },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: { trigger: img.closest('.de-detail'), start: 'top bottom', end: 'bottom 30%', scrub: true },
        }
      );
    });
  };

  /* ---------- 5. The notes: the aroma orbit ---------- */

  // The ingredients circle the words; scrolling turns the orbit so each comes to the front in turn (big, sharp,
  // named), and the words, light, smoke and big background word follow the group of whichever is in front.
  const TIER_SMOKE = { top: 'gold', heart: 'rose', base: 'ember' };

  const initOrbit = (el) => {
    const items = [...el.querySelectorAll('.de-note')];
    if (!hasGsap() || !items.length) return; // no animation: the section shows as a simple grid
    const { gsap, ScrollTrigger } = window;
    el.classList.add('is-live');
    const stage = el.querySelector('.de-orbit__stage');
    const tiers = [...el.querySelectorAll('.de-orbit__tier')];
    const steps = [...el.querySelectorAll('.de-orbit__steps li')];
    const ghost = el.querySelector('[data-de-ghost]');
    const head = el.querySelector('.de-orbit__head');
    const words = el.querySelector('.de-orbit__words');
    const count = items.length;
    const arc = (Math.PI * 2) / count;
    const state = { turn: 0 };
    const pointer = { x: 0, y: 0 };
    let front = -1;
    let tier = null;
    let ghostTimer;

    const setTier = (name) => {
      if (name === tier) return;
      tier = name;
      el.dataset.active = name;
      tiers.forEach((t) => t.classList.toggle('is-active', t.dataset.tier === name));
      steps.forEach((s) => s.classList.toggle('is-active', s.dataset.tier === name));
      const word = tiers.find((t) => t.dataset.tier === name)?.dataset.ghost || '';
      ghost.classList.add('is-swapping');
      clearTimeout(ghostTimer);
      ghostTimer = setTimeout(() => {
        ghost.textContent = word;
        ghost.classList.remove('is-swapping');
      }, 350);
      Smoke.setTint(TIER_SMOKE[name]);
    };

    // Phones: the words start just under the header, and the orbit is lifted so its front ingredient sits just
    // under the words, whatever the screen height. Desktop keeps the layout from the stylesheet.
    let lift = 0;
    let shrink = 1;
    let reach = 0; // how far the orbit reaches up and down from its centre (phones)
    const fitPhone = () => {
      if (!isSmall()) {
        words.style.top = '';
        lift = 0;
        shrink = 1;
        reach = 0;
        return;
      }
      const stageTop = stage.getBoundingClientRect().top;
      const headBottom = head.getBoundingClientRect().bottom - stageTop;
      const h = stage.offsetHeight;
      words.style.top = `${headBottom + (h < 720 ? 32 : 56)}px`; // a little tighter on short phones
      const wordsBottom = words.getBoundingClientRect().bottom - stageTop;
      const item = items[0];
      // Short phones: shrink the orbit a little so the front ingredient still fits below the words.
      const room = h - 40 - (wordsBottom + 26);
      shrink = Math.max(0.6, Math.min(1, room / (item.offsetHeight * 1.12)));
      const frontHalf = (item.offsetHeight * 1.12 * shrink) / 2;
      const backHalf = (item.offsetHeight * 0.42 * shrink) / 2;
      const front = Math.min(wordsBottom + 26 + frontHalf, h - 40 - frontHalf);
      // The back of the orbit must stay below the header: flatten the orbit rather than let it rise into it.
      reach = Math.max(40, Math.min(h * 0.2 * shrink, (front - headBottom - 24 - backHalf) / 2));
      const centre = item.offsetTop + item.offsetHeight / 2;
      lift = front - reach - centre;
      // Use the space left at the bottom: move the words and the orbit down together, keeping a margin.
      const push = Math.max(0, (h - 44 - (front + frontHalf)) * 0.75);
      words.style.top = `${parseFloat(words.style.top) + push}px`;
      lift += push;
    };

    const layout = () => {
      const w = stage.offsetWidth;
      const h = stage.offsetHeight;
      const rx = isSmall() ? w * 0.36 : Math.min(w * 0.38, 560);
      const ry = isSmall() && reach ? reach : h * (isSmall() ? 0.2 : 0.235);
      items.forEach((item, i) => {
        const angle = (i - state.turn) * arc;
        const z = Math.cos(angle);
        const depth = (z + 1) / 2; // 1 at the front, 0 at the back
        const x = Math.sin(angle) * rx + pointer.x * depth * 22;
        const y = z * ry + pointer.y * depth * 14 + lift;
        const scale = (0.42 + 0.7 * Math.pow(depth, 1.4)) * shrink;
        item.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) scale(${scale.toFixed(3)})`;
        item.style.opacity = (0.16 + 0.84 * depth).toFixed(3);
        // In front of the words only when clearly at the front of the orbit. On phones the words and the orbit
        // share the same column, so only the very front ingredient may sit on top of them.
        item.style.zIndex = z > (isSmall() ? 0.9 : 0.35) ? 200 + Math.round(z * 50) : Math.round(depth * 90);
        item.classList.toggle('is-far', depth < 0.3);
        item.classList.toggle('is-back', depth >= 0.3 && depth < 0.6);
      });
      const now = ((Math.round(state.turn) % count) + count) % count;
      if (now !== front) {
        front = now;
        items.forEach((item, i) => item.classList.toggle('is-front', i === now));
        setTier(items[now].dataset.tier || 'top');
      }
    };

    // A short pause at each end, and the orbit turns once through every ingredient in between.
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1.2,
        onLeave: () => Smoke.setTint('lamp'),
        onLeaveBack: () => Smoke.setTint('lamp'),
        onEnter: () => Smoke.setTint(TIER_SMOKE[tier] || 'gold'),
        onEnterBack: () => Smoke.setTint(TIER_SMOKE[tier] || 'gold'),
      },
    });
    tl.to({}, { duration: 0.5 });
    tl.to(state, { turn: count - 1, duration: count - 1, ease: 'none', onUpdate: layout });
    tl.to({}, { duration: 0.6 });

    // The orbit leans a little towards the pointer.
    stage.addEventListener('pointermove', (e) => {
      const r = stage.getBoundingClientRect();
      pointer.x = ((e.clientX - r.left) / r.width - 0.5) * 2;
      pointer.y = ((e.clientY - r.top) / r.height - 0.5) * 2;
      layout();
    });
    window.addEventListener('resize', () => {
      fitPhone();
      layout();
    });
    // Re-fit whenever the header or the words change size (fonts arriving, rotating the phone, edited text).
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => {
        fitPhone();
        layout();
      }).observe(head);
    }
    fitPhone();
    layout();
  };

  /* ---------- 6–7. Waitlist and the share card ---------- */

  const NAME_KEY = 'dallah-esencia-name';
  const store = {
    get: () => {
      try {
        return sessionStorage.getItem(NAME_KEY) || '';
      } catch (e) {
        return '';
      }
    },
    set: (v) => {
      try {
        sessionStorage.setItem(NAME_KEY, v);
      } catch (e) {
        // Storage blocked (private mode): the card is simply drawn without a name.
      }
    },
  };

  // Canvas letter-spacing isn't everywhere yet, so space the letters by hand.
  const spacedText = (ctx, text, x, y, spacing) => {
    const chars = [...text];
    const widths = chars.map((c) => ctx.measureText(c).width);
    const total = widths.reduce((a, b) => a + b, 0) + spacing * (chars.length - 1);
    let cx = x - total / 2;
    ctx.textAlign = 'left';
    chars.forEach((c, i) => {
      ctx.fillText(c, cx, y);
      cx += widths[i] + spacing;
    });
  };

  const loadImage = (src, cors = true) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      if (cors) img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const drawCard = async (canvas, name) => {
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const d = canvas.dataset;

    await Promise.all(
      ['600 80px Cinzel', 'italic 500 110px "Cormorant Garamond"'].map((f) => document.fonts.load(f).catch(() => {}))
    );
    const lamp = await loadImage(d.lampSrc).catch(() => loadImage(d.lampSrc, false)).catch(() => null);

    const bg = ctx.createRadialGradient(W / 2, H * 0.55, 0, W / 2, H * 0.55, H * 0.7);
    bg.addColorStop(0, '#5c0f1f');
    bg.addColorStop(0.5, '#2b0710');
    bg.addColorStop(1, '#1c040a');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // The stars from the bottle's band, fading out towards the edges.
    ctx.strokeStyle = '#dcb872';
    ctx.lineWidth = 1.4;
    for (let y = 60; y < H; y += 120) {
      for (let x = 60; x < W; x += 120) {
        const fade = 1 - Math.min(1, Math.hypot(x - W / 2, y - H / 2) / (H * 0.6));
        if (fade <= 0) continue;
        ctx.globalAlpha = 0.09 * fade;
        [0, Math.PI / 4].forEach((a) => {
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(a);
          ctx.strokeRect(-22, -22, 44, 44);
          ctx.restore();
        });
      }
    }
    ctx.globalAlpha = 1;

    const glow = ctx.createRadialGradient(W / 2, H * 0.72, 0, W / 2, H * 0.72, 520);
    glow.addColorStop(0, 'rgba(255,138,92,0.5)');
    glow.addColorStop(0.4, 'rgba(226,56,79,0.28)');
    glow.addColorStop(1, 'rgba(226,56,79,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    if (lamp) {
      const h = 1060;
      const w = (h * lamp.naturalWidth) / lamp.naturalHeight;
      ctx.drawImage(lamp, (W - w) / 2, 470, w, h);
    }

    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#dcb872';
    ctx.font = '600 36px Cinzel';
    spacedText(ctx, d.line.toUpperCase(), W / 2, 200, 14);

    if (name) {
      let size = 120;
      do {
        ctx.font = `italic 500 ${size}px "Cormorant Garamond"`;
        size -= 6;
      } while (ctx.measureText(name).width > W - 160 && size > 50);
      ctx.fillStyle = '#f7eddc';
      ctx.textAlign = 'center';
      ctx.fillText(name, W / 2, 340);
    }

    const gold = ctx.createLinearGradient(0, 1610, 0, 1700);
    gold.addColorStop(0, '#f6dfa8');
    gold.addColorStop(0.5, '#dcb872');
    gold.addColorStop(1, '#a47c3b');
    ctx.fillStyle = gold;
    ctx.font = '600 68px Cinzel';
    spacedText(ctx, d.brand, W / 2, 1690, 16);

    ctx.fillStyle = 'rgba(247,237,220,0.6)';
    ctx.font = '600 26px Cinzel';
    spacedText(ctx, d.footer, W / 2, 1790, 12);

    ctx.strokeStyle = 'rgba(220,184,114,0.55)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(36, 36, W - 72, H - 72, 28);
    else ctx.rect(36, 36, W - 72, H - 72);
    ctx.stroke();
  };

  const initWaitlist = (el) => {
    const form = el.querySelector('form');
    const join = el.querySelector('[data-de-join]');
    const thanks = el.querySelector('[data-de-thanks]');
    const nameInput = el.querySelector('[data-de-name]');
    const submit = form.querySelector('[type="submit"]');
    const error = el.querySelector('[data-de-error]');
    const canvas = el.querySelector('[data-de-card]');
    const saveBtn = el.querySelector('[data-de-save]');
    const shareBtn = el.querySelector('[data-de-share]');
    const fileName = 'dallah-esencia-my-seat.png';
    const toBlob = () => new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

    const showThanks = (name, animate) => {
      if (!thanks.hidden) return;
      join.hidden = true;
      thanks.hidden = false;
      el.scrollIntoView({ block: 'start', behavior: animate ? 'smooth' : 'auto' });
      thanks.focus({ preventScroll: true });
      if (animate && hasGsap()) window.gsap.from(thanks.children, { opacity: 0, y: 24, duration: 0.9, stagger: 0.12, ease: 'power3.out' });

      drawCard(canvas, name).then(() => {
        const rect = canvas.getBoundingClientRect();
        Smoke.emit(rect.left + rect.width / 2, rect.top + rect.height * 0.35, { count: 30, spread: 60, size: 70, life: 3200, alpha: 0.16, vy: -0.9 });
        if (navigator.canShare) {
          toBlob()
            .then((blob) => {
              const file = new File([blob], fileName, { type: 'image/png' });
              if (!navigator.canShare({ files: [file] })) return;
              shareBtn.hidden = false;
              shareBtn.onclick = () => navigator.share({ files: [file], title: canvas.dataset.brand }).catch(() => {});
            })
            .catch(() => {});
        }
      });
    };

    saveBtn.addEventListener('click', async () => {
      try {
        const blob = await toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      } catch (e) {
        saveBtn.hidden = true;
      }
    });

    // Shopify's form normally reloads onto another page (often the home page) after signing up, so send it in
    // the background instead and show the thank-you right here.
    form.addEventListener('submit', async (e) => {
      const name = nameInput.value.trim();
      store.set(name);
      if (!window.fetch || !window.FormData) return; // very old browser: let the normal form do its job
      e.preventDefault();
      error.hidden = true;
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' });
        const landed = response.url || '';
        if (landed.includes('/challenge')) {
          // Shopify wants a "prove you're human" check: hand over to the normal form, which shows it.
          form.submit();
          return;
        }
        // Success, or already on the list (Shopify marks returning customers with form_type=customer).
        if (landed.includes('customer_posted=true') || landed.includes('form_type=customer')) {
          showThanks(name, true);
          return;
        }
        // Otherwise show Shopify's own message if it sent one back (e.g. an email it won't accept).
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const message = page.querySelector(`#${form.id} .de-form__error, .form-message--main.alert--error`);
        error.textContent = message?.textContent.trim() || error.dataset.fallback;
        error.hidden = false;
      } catch (err) {
        error.textContent = error.dataset.fallback;
        error.hidden = false;
      } finally {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      }
    });

    // Arriving back after the normal (non-background) form: show the thank-you straight away.
    const params = new URLSearchParams(location.search);
    const posted = !thanks.hidden || (params.get('form_type') === 'customer' && params.has('contact[email]'));
    if (posted) {
      thanks.hidden = true;
      showThanks(store.get(), false);
    }
  };

  /* ---------- Start ---------- */

  const init = (root = document) => {
    Smoke.init();
    if (hasGsap()) window.gsap.registerPlugin(window.ScrollTrigger);
    const once = (selector, key, fn) =>
      root.querySelectorAll(selector).forEach((el) => {
        if (el[key]) return;
        el[key] = true;
        fn(el);
      });
    once('[data-de-lamp]', 'deLamp', (el) => new Lamp(el));
    once('[data-de-product]', 'deProduct', initProduct);
    once('[data-de-heritage]', 'deHeritage', initHeritage);
    once('[data-de-collage]', 'deCollage', initCollage);
    once('[data-de-film]', 'deFilm', initFilm);
    once('[data-de-buy]', 'deBuy', initBuy);
    once('[data-de-range]', 'deRange', initRange);
    once('[data-de-details]', 'deDetails', initDetails);
    once('[data-de-orbit]', 'deOrbit', initOrbit);
    once('[data-de-waitlist]', 'deWaitlist', initWaitlist);
    observeReveals(root);
  };

  // The scroll animations remember where each section sits on the page. If the page grows or shrinks afterwards
  // (a video or picture arriving, fonts loading, the header changing), those places are measured again so every
  // animation still runs at its own section.
  const watchLayout = () => {
    if (!hasGsap()) return;
    let height = document.documentElement.scrollHeight;
    let timer = 0;
    const check = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const now = document.documentElement.scrollHeight;
        if (Math.abs(now - height) < 2) return;
        height = now;
        window.ScrollTrigger.refresh();
      }, 200);
    };
    if ('ResizeObserver' in window) new ResizeObserver(check).observe(document.body);
    window.addEventListener('load', check);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(check);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      watchLayout();
    });
  } else {
    init();
    watchLayout();
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
